/**
 * Travel mode catalog.
 *
 * A participant declares which modes they accept; the engine then computes one
 * travel time per accepted mode and keeps the fastest one. Mode keys double as
 * the MapProvider route-planning argument.
 */

export const TRAVEL_MODES = ['transit', 'driving', 'bicycling', 'walking'] as const

export type TravelMode = (typeof TRAVEL_MODES)[number]

export interface TravelModeDefinition {
  key: TravelMode
  labelKey: string
  icon: string
  /**
   * Whether the mode depends on published timetables. Timetable modes are the
   * only ones subject to the last-departure hard constraint.
   */
  scheduled: boolean
}

export const TRAVEL_MODE_DEFINITIONS: Record<TravelMode, TravelModeDefinition> = {
  transit: { key: 'transit', labelKey: 'mode.transit', icon: '🚇', scheduled: true },
  driving: { key: 'driving', labelKey: 'mode.driving', icon: '🚗', scheduled: false },
  bicycling: { key: 'bicycling', labelKey: 'mode.bicycling', icon: '🚲', scheduled: false },
  walking: { key: 'walking', labelKey: 'mode.walking', icon: '🚶', scheduled: false },
}

export function isTravelMode(value: string): value is TravelMode {
  return (TRAVEL_MODES as readonly string[]).includes(value)
}

/** Modes accepted when a participant has not expressed a preference. */
export const DEFAULT_TRAVEL_MODES: TravelMode[] = ['transit', 'driving']

/**
 * Time-of-day buckets used by the local travel estimate.
 *
 * Only road speeds vary enough with the hour to be worth splitting: an estimate
 * made at eight in the morning and one made at eleven at night describe
 * different journeys for the same pair of points.
 */
export const TRAVEL_TIME_BUCKETS = ['peak', 'offpeak', 'night'] as const

export type TravelTimeBucket = (typeof TRAVEL_TIME_BUCKETS)[number]

export function travelTimeBucketOf(hour: number): TravelTimeBucket {
  if (hour >= 7 && hour < 10) {
    return 'peak'
  }
  if (hour >= 17 && hour < 20) {
    return 'peak'
  }
  if (hour >= 22 || hour < 6) {
    return 'night'
  }
  return 'offpeak'
}

/**
 * Normalises a submitted mode list, always returning at least one mode so the
 * engine has something to plan with.
 */
export function normalizeTravelModes(values: readonly string[]): TravelMode[] {
  const accepted = new Set(values.filter(isTravelMode))
  if (accepted.size === 0) {
    return [...DEFAULT_TRAVEL_MODES]
  }
  return TRAVEL_MODES.filter((mode) => accepted.has(mode))
}
